import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { mockData } from '../mockData';
import { Shield, FileText, Building2, Search, ShoppingBag, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const ServicesSlider = ({ onOpenModal }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'start',
    slidesToScroll: 1
  });
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const iconMap = {
    Shield: Shield,
    FileText: FileText,
    Building2: Building2,
    Search: Search,
    ShoppingBag: ShoppingBag
  };

  // Background images for each service
  const serviceBackgrounds = {
    1: 'https://images.unsplash.com/photo-1589307904488-7d60ff29c975', // Trademark Registration - gavel
    2: 'https://images.unsplash.com/photo-1593444285553-28163240e3f1', // Copyright Registration - typewriter
    3: 'https://images.unsplash.com/photo-1590103514966-5e2a11c13e21', // Business Formation - whiteboard
    4: 'https://images.unsplash.com/photo-1588600878108-578307a3cc9d', // Trademark Search - research
    5: 'https://images.pexels.com/photos/16158536/pexels-photo-16158536.jpeg' // Amazon Brand Registry - package
  };

  // Service page routes
  const serviceRoutes = {
    1: '/trademark-registration',
    2: '/copyright-registration',
    3: '/business-formation',
    4: '/trademark-search',
    5: '/amazon-brand-registry'
  };

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section id="services" className="py-20 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our Professional{' '}
            <span className="bg-brand-gradient bg-clip-text text-transparent">
              Services
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive intellectual property protection and business services tailored to your needs
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {mockData.services.map((service) => {
                const Icon = iconMap[service.icon];
                return (
                  <div
                    key={service.id}
                    className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0"
                  >
                    <div className="group relative rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-emerald-100 overflow-hidden h-full mx-3 animate-scale-in bg-white">
                      {/* Background Image with Overlay - subtle effect */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center transition-all duration-500 opacity-10 group-hover:opacity-20 group-hover:scale-105"
                        style={{ 
                          backgroundImage: `url(${serviceBackgrounds[service.id]})`,
                        }}
                      ></div>
                      
                      {/* Gradient Overlay - keeps content readable */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/98 via-emerald-50/95 to-teal-50/90 group-hover:from-emerald-50/98 group-hover:via-teal-50/95 group-hover:to-white/90 transition-all duration-300"></div>
                      
                      {/* Content */}
                      <div className="relative z-10">
                        <div className="bg-brand-gradient w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg group-hover:shadow-xl">
                          <Icon className="text-white" size={32} />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-brand-primary transition-colors duration-300">{service.title}</h3>
                        <p className="text-base text-gray-700 mb-6 leading-relaxed font-medium">{service.description}</p>
                        <div className="space-y-2 mb-6">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center space-x-2">
                              <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full group-hover:scale-150 transition-transform duration-300"></div>
                              <span className="text-gray-800 text-sm font-medium">{feature}</span>
                            </div>
                          ))}
                        </div>
                        <Link to={serviceRoutes[service.id]}>
                          <Button
                            variant="ghost"
                            className="text-brand-primary hover:text-white hover:bg-brand-primary group/btn p-3 border-2 border-brand-primary transition-all shadow-md hover:shadow-lg"
                          >
                            Learn More
                            <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={16} />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation Buttons */}
          <Button
            onClick={scrollPrev}
            disabled={!prevBtnEnabled}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white hover:bg-emerald-50 text-brand-primary rounded-full p-3 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed z-10 border-2 border-brand-primary"
            size="icon"
          >
            <ChevronLeft size={24} />
          </Button>
          <Button
            onClick={scrollNext}
            disabled={!nextBtnEnabled}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white hover:bg-emerald-50 text-brand-primary rounded-full p-3 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed z-10 border-2 border-brand-primary"
            size="icon"
          >
            <ChevronRight size={24} />
          </Button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {mockData.services.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === selectedIndex
                  ? 'bg-emerald-600 w-8'
                  : 'bg-gray-300 hover:bg-emerald-400'
              }`}
              onClick={() => emblaApi && emblaApi.scrollTo(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSlider;
